import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AuthLayout from "../../layouts/AuthLayout";
import api from "../../lib/axios";

export default function Register() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    acceptedTerms: false,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  function handleChange(e) {
    const { name, value, type, checked } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();

    setError("");

    if (!form.acceptedTerms) {
      setError("You must agree to the Terms and Privacy Policy.");
      return;
    }

    setLoading(true);

    try {
      await api.post("/Auth/register", {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        password: form.password,
        acceptedTerms: form.acceptedTerms,
      });

      navigate("/onboarding/business");
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data ||
          "Unable to create account. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthLayout
      title="Create your account"
      subtitle="Start managing Google review responses with RevUShield."
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field
            label="First Name"
            name="firstName"
            type="text"
            placeholder="John"
            value={form.firstName}
            onChange={handleChange}
            required
          />

          <Field
            label="Last Name"
            name="lastName"
            type="text"
            placeholder="Smith"
            value={form.lastName}
            onChange={handleChange}
            required
          />
        </div>

        <Field
          label="Email"
          name="email"
          type="email"
          placeholder="you@business.com"
          value={form.email}
          onChange={handleChange}
          required
        />

        <Field
          label="Password"
          name="password"
          type="password"
          placeholder="••••••••"
          value={form.password}
          onChange={handleChange}
          required
          minLength={8}
        />

        <label className="flex items-start gap-3 text-sm text-slate-600">
          <input
            name="acceptedTerms"
            type="checkbox"
            checked={form.acceptedTerms}
            onChange={handleChange}
            required
            className="mt-1 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
          />

          <span className="leading-relaxed">
            I agree to the{" "}
            <Link
              to="/legal/terms"
              className="text-indigo-600 font-medium hover:text-indigo-700"
            >
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link
              to="/legal/privacy"
              className="text-indigo-600 font-medium hover:text-indigo-700"
            >
              Privacy Policy
            </Link>
            .
          </span>
        </label>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed text-white px-4 py-3 rounded-xl text-sm font-medium transition shadow-sm"
        >
          {loading ? "Creating account..." : "Create Account"}
        </button>
      </form>

      <p className="text-sm text-slate-500 text-center mt-6">
        Already have an account?{" "}
        <Link
          to="/login"
          className="text-indigo-600 font-medium hover:text-indigo-700"
        >
          Log in
        </Link>
      </p>
    </AuthLayout>
  );
}

function Field({ label, ...props }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-slate-700">{label}</span>

      <input
        {...props}
        className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
      />
    </label>
  );
}
